import { motion } from 'framer-motion'
import type { TillyProps } from '../../types'

export default function Tilly({ expression, isSpeaking, onClick }: TillyProps) {
  // Expression to emoji mapping (placeholder for Rive/Lottie)
  const expressionMap = {
    neutral: '🐯',
    happy: '😊',
    thinking: '🤔',
    encouraging: '👍',
    surprised: '😲',
    celebrating: '🎉',
  }

  // Expression-specific animation variants
  const characterVariants = {
    neutral: { scale: 1, y: 0 },
    happy: { scale: 1.05, y: -4 },
    thinking: { rotate: [0, -3, 3, 0] },
    encouraging: { scale: [1, 1.08, 1] },
    surprised: { scale: 1.1, y: -8 },
    celebrating: { scale: 1.15, rotate: [0, -10, 10, 0] },
  }

  return (
    <div 
      className="flex flex-col items-center justify-end pb-2"
      onClick={onClick}
    >
      {/* Tilly Character Container - Larger for better engagement */}
      <motion.div 
        className={`
          relative w-24 h-24 sm:w-32 sm:h-32 
          flex items-center justify-center flex-shrink-0
          ${onClick ? 'cursor-pointer' : ''}
        `}
        animate={isSpeaking ? { scale: [1, 1.03, 1] } : characterVariants[expression]}
        transition={{ 
          duration: isSpeaking ? 0.3 : 0.3, 
          repeat: isSpeaking ? Infinity : 0,
          ease: "easeInOut"
        }}
      >
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-[var(--math-primary)] rounded-full opacity-20 blur-xl scale-110" />
        
        {/* Character Circle Background */}
        <div className="absolute inset-2 bg-gradient-to-br from-orange-100 to-orange-50 rounded-full shadow-md" />
        
        {/* Inner Ring */}
        <div className="absolute inset-3 border-2 border-orange-200/50 rounded-full" />
        
        {/* Expression Emoji - Larger for better visibility */}
        <motion.span 
          className="text-6xl sm:text-7xl relative z-10 select-none"
          role="img"
          aria-label={`Tilly is ${expression}`}
          animate={isSpeaking ? { y: [0, -2, 0] } : {}}
          transition={{ duration: 0.25, repeat: Infinity }}
        >
          {expressionMap[expression]}
        </motion.span>

        {/* Speaking Indicator */}
        {isSpeaking && (
          <motion.div 
            className="absolute -top-1 -right-1 w-7 h-7 bg-[var(--math-primary)] rounded-full flex items-center justify-center shadow-lg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 15 }}
          >
            <motion.span 
              className="text-white text-xs"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              🔊
            </motion.span>
          </motion.div>
        )}
      </motion.div>

      {/* Name Label */}
      <motion.span 
        className="mt-2 text-xs font-bold text-gray-700 bg-white/90 px-3 py-1 rounded-full shadow-sm backdrop-blur-sm"
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.2 }}
      >
        Tilly
      </motion.span>
    </div>
  )
}
